/**
 * [PURPOSE]: Sub-plan 1H-H-cond — kind-switched condition authoring slot.
 *   Replaces the flat EFFECT_CONDITIONS <select> in EffectComposer and the flat
 *   CONDITION_FLAGS <select> in EffectBuilderControls' EffectRow. Mirrors the
 *   PrereqComposer pattern: kind dropdown + per-kind operand control.
 *
 * [WHY]: The pre-1H-H-cond condition slot was either 4-entry (EffectComposer)
 *   or 19-entry flags only (EffectRow). Neither could express "when wielding a
 *   longbow" or "vs. spells" — the S1 scar: free-text where a catalog exists.
 *   This component provides structured, catalog-constrained authoring without
 *   free-text escape hatches (CODEBASE_RULES Rule 30 / the S1 scar).
 *
 * [PINNED CONTRACT — the leaf the runtime evaluator consumes]:
 *   effect.condition = { category, op, operands[] }   (generic leaf)
 *   effect.condition = { when: '<flag>' }              (playerFlag — backward-compat)
 *   effect.condition = null                            (always — no condition)
 *
 *   kind → category mapping:
 *   - 'always'      → null (no condition emitted)
 *   - 'weaponType'  → { category: 'equippedWeaponType', op: 'hasAny', operands: [...bare ids] }
 *   - 'armorType'   → { category: 'equippedArmorType',  op: 'hasAny', operands: [...ids] }
 *   - 'damageType'  → { category: 'damageType',         op: 'hasAny', operands: [...ids] }
 *   - 'saveContext' → { category: 'rollSourceType',     op: 'hasAny', operands: [...ids] }
 *   - 'targetType'  → { category: 'targetType',         op: 'hasAny', operands: [...ids] }
 *   - 'playerFlag'  → { when: '<flag>' }  (backward-compat object path)
 *
 * [OPERAND NORMALIZATION]:
 *   weaponType: WEAPON_BASE_TYPES catalog uses bare ids (e.g. 'longbow'). Emitted
 *   operands are always bare baseType ids — no prefix. The runtime evaluator
 *   compares against context.equippedWeaponBaseType directly.
 *
 * [BACKWARD-COMPAT LOAD NORMALIZER]:
 *   parseConditionToInternal(stored) maps:
 *   - null / undefined / 'always'     → { kind: 'always' }
 *   - string (bare e.g. 'unarmored')  → { kind: 'playerFlag', flag: 'unarmored' }
 *   - { when: '<flag>' }              → { kind: 'playerFlag', flag: '<flag>' }
 *   - { category, op, operands }      → expanded internal per kind
 *
 * [API]:
 *   <ConditionKindSlot
 *     condition={effect.condition}   // stored leaf (any legacy shape OR new leaf)
 *     onChange={(leaf) => void}      // fires emitted leaf (null | {when} | {category,op,operands})
 *     disabled={false}
 *     testIdPrefix="some-testid"     // data-testid="${testIdPrefix}-condition" on root
 *   />
 *
 * [BREADCRUMBS]:
 *   (a) saveContext + targetType AUTHOR now; their runtime consumers are Phase 2
 *       (1H-H-cond GAP-8 / targetType arc). effectRegistry._buildContext needs
 *       context enrichment before these conditions evaluate. Until then they
 *       are stored correctly but silently pass-through (same behaviour as today).
 *   (b) triggeredEffect filterKeys (damageTypeMatch/targetTypeMatch/ammoTypeMatch)
 *       in EffectBuilderControls ~L913-930 are still free-text — same S1 scar.
 *       Migrate those to pickers in a follow-up arc (queued in master-queue).
 *   (c) [green-composer-C — 2026-05-29] combineNone/NOR combinator added to UI
 *       (armorType registry swap also lands in this pass). The evaluator already
 *       handles { none: [...] }; this closes the UI breadcrumb from cb6.
 *       Nested combine slots are still limited to LEAF_KINDS (no
 *       combine-within-combine) to prevent unbounded UI depth.
 */

import React from 'react';
import {
  CONDITION_KINDS,
  SAVE_CONTEXT_OPTIONS,
} from '../../data/customFeatVocabulary';
import { CONDITION_FLAGS } from '../../data/subclassEffectBuilderVocab';
import ReferencePicker from './ReferencePicker';

// ============================================================================
// Internal state helpers
// ============================================================================

/**
 * Returns the default internal state for a given kind.
 * Mirrors PrereqComposer.defaultFieldsForKind.
 */
export function defaultFieldsForKind(kind) {
  switch (kind) {
    case 'always':      return { kind: 'always' };
    case 'playerFlag':  return { kind: 'playerFlag', flag: '' };
    case 'weaponType':  return { kind: 'weaponType', weapons: [] };
    case 'armorType':   return { kind: 'armorType', armorTypes: [] };
    case 'saveContext': return { kind: 'saveContext', context: '' };
    case 'damageType':  return { kind: 'damageType', damageTypes: [] };
    case 'targetType':  return { kind: 'targetType', targetType: '' };
    // [1H-H-cond-combinators]: 1-level AND/OR/NOR combine. condA/condB are leaf
    // conditions (null = always/no-condition). They store the EMITTED leaf
    // shape so ConditionKindSlot can round-trip them via parseConditionToInternal.
    case 'combineAll':  return { kind: 'combineAll',  condA: null, condB: null };
    case 'combineAny':  return { kind: 'combineAny',  condA: null, condB: null };
    // [green-composer-C — 2026-05-29]: NOR combinator — "NONE of these apply".
    // Evaluator handles { none: [...] } already (shipped in 1H-H-cond-combinators).
    case 'combineNone': return { kind: 'combineNone', condA: null, condB: null };
    default:            return { kind: 'always' };
  }
}

/**
 * Maps stored condition leaf (any legacy shape) → internal {kind, ...fields}.
 * Call at load / init time so ConditionKindSlot always works with a typed object.
 */
export function parseConditionToInternal(stored) {
  if (!stored || stored === 'always') {
    return { kind: 'always' };
  }
  // Legacy bare string: 'unarmored', 'noShield', 'dualWielding', ...
  if (typeof stored === 'string') {
    return { kind: 'playerFlag', flag: stored };
  }
  // Legacy {when: '<flag>'} shape (CustomFeatCreator save path pre-1H-H-cond)
  if (stored.when !== undefined) {
    const flag = stored.when || '';
    if (!flag) return { kind: 'always' };
    return { kind: 'playerFlag', flag };
  }
  // New {category, op, operands} leaf
  if (stored.category) {
    switch (stored.category) {
      case 'equippedWeaponType':
        return { kind: 'weaponType', weapons: Array.isArray(stored.operands) ? stored.operands : [] };
      case 'equippedArmorType':
        return { kind: 'armorType', armorTypes: Array.isArray(stored.operands) ? stored.operands : [] };
      case 'damageType':
        return { kind: 'damageType', damageTypes: Array.isArray(stored.operands) ? stored.operands : [] };
      case 'rollSourceType':
        return { kind: 'saveContext', context: Array.isArray(stored.operands) && stored.operands[0] ? stored.operands[0] : '' };
      case 'targetType':
        return { kind: 'targetType', targetType: Array.isArray(stored.operands) && stored.operands[0] ? stored.operands[0] : '' };
      default:
        return { kind: 'always' };
    }
  }
  // [1H-H-cond-combinators / green-composer-C]: combinator nodes
  // { all: [...] } / { any: [...] } / { none: [...] }
  // condA/condB store the raw emitted leaf (null = always/no condition).
  if ('all' in stored) {
    const children = Array.isArray(stored.all) ? stored.all : [];
    return { kind: 'combineAll', condA: children[0] || null, condB: children[1] || null };
  }
  if ('any' in stored) {
    const children = Array.isArray(stored.any) ? stored.any : [];
    return { kind: 'combineAny', condA: children[0] || null, condB: children[1] || null };
  }
  // [green-composer-C — 2026-05-29]: NOR combinator — evaluator handles { none:[...] }
  if ('none' in stored) {
    const children = Array.isArray(stored.none) ? stored.none : [];
    return { kind: 'combineNone', condA: children[0] || null, condB: children[1] || null };
  }
  return { kind: 'always' };
}

/**
 * Maps internal {kind, ...fields} → stored leaf (the pinned contract).
 * Returns null for 'always'. Returns {when} for playerFlag. Returns
 * {category, op, operands} for all catalog kinds.
 */
export function emitCondition(internal) {
  const { kind } = internal;
  switch (kind) {
    case 'always':
      return null;
    case 'playerFlag': {
      const flag = internal.flag || '';
      if (!flag) return null;
      return { when: flag };
    }
    case 'weaponType': {
      const weapons = Array.isArray(internal.weapons) ? internal.weapons : [];
      if (weapons.length === 0) return null;
      // Operands are BARE baseType ids (WEAPON_BASE_TYPES uses bare ids already)
      return { category: 'equippedWeaponType', op: 'hasAny', operands: weapons };
    }
    case 'armorType': {
      const armorTypes = Array.isArray(internal.armorTypes) ? internal.armorTypes : [];
      if (armorTypes.length === 0) return null;
      return { category: 'equippedArmorType', op: 'hasAny', operands: armorTypes };
    }
    case 'damageType': {
      const damageTypes = Array.isArray(internal.damageTypes) ? internal.damageTypes : [];
      if (damageTypes.length === 0) return null;
      return { category: 'damageType', op: 'hasAny', operands: damageTypes };
    }
    case 'saveContext': {
      const ctx = internal.context || '';
      if (!ctx) return null;
      // saveContext — consumer is Phase 2 (rollSourceType context enrichment)
      return { category: 'rollSourceType', op: 'hasAny', operands: [ctx] };
    }
    case 'targetType': {
      const tt = internal.targetType || '';
      if (!tt) return null;
      // targetType — consumer is Phase 2 (targetType arc)
      return { category: 'targetType', op: 'hasAny', operands: [tt] };
    }
    // [1H-H-cond-combinators]: 1-level AND/OR. condA and condB are stored leaf
    // shapes (null = no condition). Null children are omitted from the emitted
    // children array — a combinator with 0 children emits null (no condition).
    // A combinator with 1 child emits just that child (unwraps the combine).
    case 'combineAll': {
      const children = [internal.condA, internal.condB].filter(Boolean);
      if (children.length === 0) return null;
      if (children.length === 1) return children[0]; // unwrap single-child
      return { all: children };
    }
    case 'combineAny': {
      const children = [internal.condA, internal.condB].filter(Boolean);
      if (children.length === 0) return null;
      if (children.length === 1) return children[0]; // unwrap single-child
      return { any: children };
    }
    // [green-composer-C — 2026-05-29]: NOR combinator. Same single-child unwrap
    // semantics as combineAll/combineAny — { none: [x] } is equivalent to NOT x,
    // and the evaluator supports it; but 1-child NOR is unusual authoring.
    // Unwrap to keep parity with the combine pattern.
    case 'combineNone': {
      const children = [internal.condA, internal.condB].filter(Boolean);
      if (children.length === 0) return null;
      if (children.length === 1) return children[0]; // unwrap single-child (NOR of 1 = NOT — unusual; unwrap for symmetry)
      return { none: children };
    }
    default:
      return null;
  }
}

// ============================================================================
// Component
// ============================================================================

const BASE_SELECT_CLASS =
  'px-2 py-1 bg-gray-800 text-white rounded border border-gray-700 outline-none focus:border-emerald-500 text-sm disabled:opacity-50';

// [green-composer-C — 2026-05-29]: ChipMultiSelect removed. armorType now uses
// ReferencePicker kind="armorType" (registered in composerCatalogRegistry.js as
// KIND_CATALOGS.armorType: ARMOR_TYPES_AC). The breadcrumb from 1H-H-cond Phase 2
// bt2 is now CLOSED — the registry kind already existed (Sub-plan 2F kind (g));
// this pass completes the consumer swap (ConditionKindSlot armorType case).

// LEAF_KINDS — the kinds that produce a single-leaf output (no recursive combine).
// When depth >= maxDepth, only these kinds are offered in the dropdown (no
// combine-within-combine). Excludes all three combinator kinds.
const LEAF_KINDS = CONDITION_KINDS.filter(
  (k) => k.value !== 'combineAll' && k.value !== 'combineAny' && k.value !== 'combineNone'
);

// DEFAULT_MAX_DEPTH — the default maximum nesting depth for the combinator UI.
// depth=0 is the top-level slot. At depth >= maxDepth the kind dropdown is
// restricted to LEAF_KINDS only (no further combine). Set to 3 to allow
// author up to 2 levels of combine-within-combine (root + 2 nested).
export const DEFAULT_MAX_DEPTH = 3;

/**
 * [Sub-plan 1H-H-cond-deep-nesting-ui — 2026-05-30]: Renders a single condition
 * sub-control row. When depth < maxDepth, the kind dropdown includes ALL
 * CONDITION_KINDS (including combinators); when depth >= maxDepth it is restricted
 * to LEAF_KINDS (no combine-within-combine). At combine kinds the children are
 * rendered as ConditionKindSlot instances at depth+1 — the recursion is bounded
 * by maxDepth.
 *
 * Previously named ConditionKindSlotLeaf (hard-coded leaf-only); now carries a
 * depth counter so the UI follows the evaluator's arbitrary-depth support.
 */
function ConditionKindSlotNode({ condition, onChange, disabled, depth, maxDepth }) {
  const effectiveDepth = typeof depth === 'number' ? depth : 0;
  const effectiveMaxDepth = typeof maxDepth === 'number' && maxDepth > 0 ? maxDepth : DEFAULT_MAX_DEPTH;

  // At max depth, restrict to leaf kinds only (no further combining)
  const atMaxDepth = effectiveDepth >= effectiveMaxDepth;
  const availableKinds = atMaxDepth ? LEAF_KINDS : CONDITION_KINDS;

  const internal = parseConditionToInternal(condition);
  const kind = internal.kind || 'always';

  // If stored condition is a combinator but we are at max depth, show as 'always'
  const isCombinator = kind === 'combineAll' || kind === 'combineAny' || kind === 'combineNone';
  const safeKind = (atMaxDepth && isCombinator) ? 'always' : kind;
  const safeInternal = safeKind !== kind ? { kind: 'always' } : internal;

  const fire = (patch) => {
    const nextInternal = { ...safeInternal, ...patch };
    onChange && onChange(emitCondition(nextInternal));
  };

  const handleKindChange = (newKind) => {
    const fresh = defaultFieldsForKind(newKind);
    onChange && onChange(emitCondition(fresh));
  };

  // Shared leaf-kind sub-controls (same across all depths)
  const renderLeafControls = (internal_, fire_) => {
    switch (internal_.kind) {
      case 'always': return null;
      case 'playerFlag': {
        const flagValue = internal_.flag || '';
        return (
          <select value={flagValue} onChange={(e) => fire_({ flag: e.target.value })} disabled={disabled} className={BASE_SELECT_CLASS}>
            <option value="">-- Player state --</option>
            {CONDITION_FLAGS.filter((f) => f.value !== '').map((f) => (
              <option key={f.value} value={f.value} title={f.description || ''}>{f.value}</option>
            ))}
          </select>
        );
      }
      case 'weaponType':
        return (
          <ReferencePicker kind="weaponBaseType" value={internal_.weapons || []} onChange={(next) => fire_({ weapons: next })} multi disabled={disabled} placeholder="-- Add weapon type --" chipVariant="blue" />
        );
      case 'armorType':
        return (
          <ReferencePicker kind="armorType" value={internal_.armorTypes || []} onChange={(next) => fire_({ armorTypes: next })} multi disabled={disabled} placeholder="-- Add armor type --" chipVariant="blue" />
        );
      case 'saveContext': {
        const ctxValue = internal_.context || '';
        return (
          <select value={ctxValue} onChange={(e) => fire_({ context: e.target.value })} disabled={disabled} className={BASE_SELECT_CLASS}>
            <option value="">-- Save context --</option>
            {SAVE_CONTEXT_OPTIONS.map((opt) => (<option key={opt.value} value={opt.value}>{opt.label}</option>))}
          </select>
        );
      }
      case 'damageType':
        return (
          <ReferencePicker kind="damageType" value={internal_.damageTypes || []} onChange={(next) => fire_({ damageTypes: next })} multi disabled={disabled} placeholder="-- Add damage type --" chipVariant="amber" />
        );
      case 'targetType':
        return (
          <ReferencePicker kind="creatureType" value={internal_.targetType || ''} onChange={(next) => fire_({ targetType: next })} disabled={disabled} placeholder="-- Target type --" />
        );
      default: return null;
    }
  };

  // Combinator sub-controls — children are full ConditionKindSlot instances at depth+1
  // (bounded by maxDepth). This is the recursive part: each child can itself be a
  // combinator if depth+1 < maxDepth.
  const renderCombinatorControls = () => {
    const combineLabel = safeKind === 'combineAll' ? 'AND' : safeKind === 'combineAny' ? 'OR' : 'NOR (NONE apply)';
    const combineHint  = safeKind === 'combineAll' ? 'Both conditions must apply'
      : safeKind === 'combineAny' ? 'At least one condition must apply'
      : 'Neither condition may apply';
    const combineCondA = safeInternal.condA || null;
    const combineCondB = safeInternal.condB || null;
    const childDepth = effectiveDepth + 1;
    return (
      <div className="flex flex-col gap-1 border border-gray-600 rounded px-2 py-1 bg-gray-900/40 w-full">
        <div className="text-xs text-gray-400 font-medium mb-1">{combineLabel}: {combineHint}</div>
        <div className="flex flex-wrap items-start gap-1">
          <span className="text-xs text-gray-500 self-center mr-1">Condition A:</span>
          <ConditionKindSlotNode
            condition={combineCondA}
            onChange={(leaf) => fire({ condA: leaf })}
            disabled={disabled}
            depth={childDepth}
            maxDepth={effectiveMaxDepth}
          />
        </div>
        <div className="flex flex-wrap items-start gap-1">
          <span className="text-xs text-gray-500 self-center mr-1">Condition B:</span>
          <ConditionKindSlotNode
            condition={combineCondB}
            onChange={(leaf) => fire({ condB: leaf })}
            disabled={disabled}
            depth={childDepth}
            maxDepth={effectiveMaxDepth}
          />
        </div>
      </div>
    );
  };

  const renderControls = () => {
    if (safeKind === 'combineAll' || safeKind === 'combineAny' || safeKind === 'combineNone') {
      return renderCombinatorControls();
    }
    return renderLeafControls(safeInternal, fire);
  };

  return (
    <span className="inline-flex flex-wrap items-start gap-2">
      <select value={safeKind} onChange={(e) => handleKindChange(e.target.value)} disabled={disabled} className={BASE_SELECT_CLASS}>
        {availableKinds.map((k) => (
          <option key={k.value} value={k.value}>{k.label}</option>
        ))}
      </select>
      {renderControls()}
    </span>
  );
}

/**
 * [Sub-plan 1H-H-cond-deep-nesting-ui — 2026-05-30]: ConditionKindSlot is now
 * recursive. It accepts optional `depth` (default 0) and `maxDepth` (default
 * DEFAULT_MAX_DEPTH=3) props. When the author picks a combinator kind (combineAll /
 * combineAny / combineNone), the children are rendered as ConditionKindSlotNode
 * instances at depth+1. At depth+1 >= maxDepth the child dropdown is restricted to
 * LEAF_KINDS only — this bounds UI depth while the evaluator handles arbitrary depth.
 *
 * Example: maxDepth=3 allows authoring up to depth 0/1/2 (3 levels of nesting,
 * e.g. "((A AND B) OR C)"). Set maxDepth=1 for D&D 5e backward-compat behaviour.
 *
 * @prop {any}      condition    - stored leaf shape (any legacy or new)
 * @prop {function} onChange     - fires emitted leaf (null|{when}|{category,op,operands}|{all/any/none})
 * @prop {boolean}  disabled     - disables all controls
 * @prop {string}   testIdPrefix - outer testid "${testIdPrefix}-condition"
 * @prop {number}   [depth=0]    - current nesting depth (0 = top-level)
 * @prop {number}   [maxDepth=3] - maximum depth before children are leaf-only
 */
function ConditionKindSlot({
  condition,
  onChange,
  disabled = false,
  testIdPrefix,
  depth = 0,
  maxDepth = DEFAULT_MAX_DEPTH,
}) {
  const effectiveDepth = typeof depth === 'number' ? depth : 0;
  const effectiveMaxDepth = typeof maxDepth === 'number' && maxDepth > 0 ? maxDepth : DEFAULT_MAX_DEPTH;

  // Parse stored → internal (at render time — no useState, pure derivation)
  const internal = parseConditionToInternal(condition);
  const kind = internal.kind || 'always';

  const fire = (patch) => {
    const nextInternal = { ...internal, ...patch };
    onChange && onChange(emitCondition(nextInternal));
  };

  const handleKindChange = (newKind) => {
    const fresh = defaultFieldsForKind(newKind);
    onChange && onChange(emitCondition(fresh));
  };

  // Per-kind sub-control
  const renderKindControls = () => {
    switch (kind) {
      case 'always':
        return null;

      case 'playerFlag': {
        const flagValue = internal.flag || '';
        return (
          <select
            value={flagValue}
            onChange={(e) => fire({ flag: e.target.value })}
            disabled={disabled}
            className={BASE_SELECT_CLASS}
          >
            <option value="">-- Player state --</option>
            {CONDITION_FLAGS.filter((f) => f.value !== '').map((f) => (
              <option key={f.value} value={f.value} title={f.description || ''}>
                {f.value}
              </option>
            ))}
          </select>
        );
      }

      case 'weaponType':
        return (
          <ReferencePicker
            kind="weaponBaseType"
            value={internal.weapons || []}
            onChange={(next) => fire({ weapons: next })}
            multi
            disabled={disabled}
            placeholder="-- Add weapon type --"
            chipVariant="blue"
          />
        );

      case 'armorType':
        return (
          <ReferencePicker
            kind="armorType"
            value={internal.armorTypes || []}
            onChange={(next) => fire({ armorTypes: next })}
            multi
            disabled={disabled}
            placeholder="-- Add armor type --"
            chipVariant="blue"
          />
        );

      case 'saveContext': {
        const ctxValue = internal.context || '';
        return (
          <select
            value={ctxValue}
            onChange={(e) => fire({ context: e.target.value })}
            disabled={disabled}
            className={BASE_SELECT_CLASS}
          >
            <option value="">-- Save context --</option>
            {SAVE_CONTEXT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        );
      }

      case 'damageType':
        return (
          <ReferencePicker
            kind="damageType"
            value={internal.damageTypes || []}
            onChange={(next) => fire({ damageTypes: next })}
            multi
            disabled={disabled}
            placeholder="-- Add damage type --"
            chipVariant="amber"
          />
        );

      case 'targetType':
        return (
          <ReferencePicker
            kind="creatureType"
            value={internal.targetType || ''}
            onChange={(next) => fire({ targetType: next })}
            disabled={disabled}
            placeholder="-- Target type --"
          />
        );

      // [Sub-plan 1H-H-cond-deep-nesting-ui — 2026-05-30]: Recursive AND/OR/NOR combine.
      // Children are ConditionKindSlotNode instances at depth+1. When depth+1 < maxDepth
      // they can themselves be combinators; when depth+1 >= maxDepth they are leaf-only.
      // The evaluator handles arbitrary depth; the UI bounds depth via maxDepth prop.
      case 'combineAll':
      case 'combineAny':
      case 'combineNone': {
        const combineLabel = kind === 'combineAll' ? 'AND' : kind === 'combineAny' ? 'OR' : 'NOR (NONE apply)';
        const combineHint  = kind === 'combineAll' ? 'Both conditions must apply'
          : kind === 'combineAny' ? 'At least one condition must apply'
          : 'Neither condition may apply';
        const combineCondA = internal.condA || null;
        const combineCondB = internal.condB || null;
        const childDepth = effectiveDepth + 1;
        return (
          <div className="flex flex-col gap-1 border border-gray-600 rounded px-2 py-1 bg-gray-900/40">
            <div className="text-xs text-gray-400 font-medium mb-1">{combineLabel}: {combineHint}</div>
            <div className="flex flex-wrap items-start gap-1">
              <span className="text-xs text-gray-500 self-center mr-1">Condition A:</span>
              <ConditionKindSlotNode
                condition={combineCondA}
                onChange={(leaf) => fire({ condA: leaf })}
                disabled={disabled}
                depth={childDepth}
                maxDepth={effectiveMaxDepth}
              />
            </div>
            <div className="flex flex-wrap items-start gap-1">
              <span className="text-xs text-gray-500 self-center mr-1">Condition B:</span>
              <ConditionKindSlotNode
                condition={combineCondB}
                onChange={(leaf) => fire({ condB: leaf })}
                disabled={disabled}
                depth={childDepth}
                maxDepth={effectiveMaxDepth}
              />
            </div>
          </div>
        );
      }

      default:
        return null;
    }
  };

  const rootTestId = testIdPrefix ? `${testIdPrefix}-condition` : undefined;

  return (
    <span data-testid={rootTestId} className="inline-flex flex-wrap items-start gap-2">
      <select
        data-testid={rootTestId ? `${rootTestId}-kind` : undefined}
        value={kind}
        onChange={(e) => handleKindChange(e.target.value)}
        disabled={disabled}
        className={BASE_SELECT_CLASS}
      >
        {CONDITION_KINDS.map((k) => (
          <option key={k.value} value={k.value}>{k.label}</option>
        ))}
      </select>
      {renderKindControls()}
    </span>
  );
}

/**
 * [Sub-plan 1H-H-cond-deep-nesting-ui — 2026-05-30]: ConditionTree is a named
 * wrapper around ConditionKindSlot that makes the maxDepth contract explicit at
 * the call site. Equivalent to <ConditionKindSlot maxDepth={maxDepth} ...> but
 * communicates intent — this is a tree composer, not a single-level slot.
 *
 * Usage:
 *   <ConditionTree
 *     condition={effect.condition}
 *     onChange={(leaf) => handleChange(leaf)}
 *     maxDepth={3}     // default 3 — allows 2 levels of nesting (depth 0, 1, 2)
 *     testIdPrefix="my-effect"
 *   />
 *
 * For D&D 5e 1-level compat: maxDepth={1}.
 * For Daggerheart / Rule System Editor: maxDepth={3} or configurable per ruleset.
 */
export function ConditionTree({
  condition,
  onChange,
  disabled = false,
  testIdPrefix,
  maxDepth = DEFAULT_MAX_DEPTH,
}) {
  return (
    <ConditionKindSlot
      condition={condition}
      onChange={onChange}
      disabled={disabled}
      testIdPrefix={testIdPrefix}
      depth={0}
      maxDepth={maxDepth}
    />
  );
}

export default ConditionKindSlot;
